import * as React from 'react';
import { WandSparkles } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Input,
  toast,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { useBusyDialogGuard } from '../../../../../hooks/useBusyDialogGuard';
import { dashboardT } from '../../../../../lib/messages';
import { useAdsTxtDemandPartnersQuery, useAdsTxtMutations } from '../../hooks';
import {
  AdsTxtAccountType,
  parseAdsTxtRecordLine,
  type AdsTxtRecord,
  type AdsTxtRecordPayload,
} from '../../model';
import { AdsTxtRecordFields } from './AdsTxtRecordFields';

const emptyValue: AdsTxtRecordPayload = {
  domain: '',
  publisherAccountId: '',
  accountType: AdsTxtAccountType.Direct,
  demandPartnerId: '',
  certAuthority: null,
  priority: null,
  comment: null,
  note: null,
  web: false,
  ctv: false,
};

export function AdsTxtRecordDialog({
  record,
  onClose,
}: {
  record: AdsTxtRecord | null;
  onClose: () => void;
}) {
  const [manual, setManual] = React.useState(Boolean(record));
  const [line, setLine] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);
  const [value, setValue] = React.useState<AdsTxtRecordPayload>(() => toPayload(record));
  const demandPartners = useAdsTxtDemandPartnersQuery();
  const mutations = useAdsTxtMutations();
  const pending = mutations.createRecord.isPending || mutations.updateRecord.isPending;
  const guard = useBusyDialogGuard(pending, onClose);
  const valid =
    /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(value.domain) &&
    Boolean(value.publisherAccountId.trim()) &&
    Boolean(value.demandPartnerId);

  function patch(next: Partial<AdsTxtRecordPayload>) {
    setValue((current) => ({ ...current, ...next }));
  }

  async function submit() {
    setSubmitted(true);
    if (!valid) return;
    const payload = trimPayload(value);
    try {
      if (record) await mutations.updateRecord.mutateAsync({ id: record.id, payload });
      else await mutations.createRecord.mutateAsync(payload);
      toast.success(dashboardT(record ? 'adsTxt.records.updated' : 'adsTxt.records.created'));
      onClose();
    } catch (error) {
      showRequestError(error, dashboardT('adsTxt.records.saveError'));
    }
  }

  return (
    <Dialog open onOpenChange={guard.onOpenChange}>
      <DialogContent
        className="flex max-h-[92vh] max-w-xl flex-col gap-0 overflow-hidden p-0"
        onEscapeKeyDown={guard.onDismissInteraction}
        onPointerDownOutside={guard.onDismissInteraction}
      >
        <DialogHeader className="border-border border-b px-5 py-4">
          <DialogTitle>
            {dashboardT(record ? 'adsTxt.records.editTitle' : 'adsTxt.records.createTitle')}
          </DialogTitle>
          <DialogDescription>{dashboardT('adsTxt.records.formDescription')}</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
          {!manual ? (
            <div className="space-y-2">
              <Label htmlFor="ads-txt-record-line">{dashboardT('adsTxt.records.record')}</Label>
              <div className="flex gap-2">
                <Input
                  id="ads-txt-record-line"
                  value={line}
                  placeholder="example.com, account-id, DIRECT, authority-id"
                  onChange={(event) => {
                    setLine(event.target.value);
                  }}
                />
                <Button
                  type="button"
                  size="icon"
                  aria-label={dashboardT('adsTxt.records.parse')}
                  onClick={() => {
                    patch(parseAdsTxtRecordLine(line));
                    setManual(true);
                  }}
                >
                  <WandSparkles className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
              <Button
                type="button"
                variant="link"
                className="h-auto p-0"
                onClick={() => {
                  setManual(true);
                }}
              >
                {dashboardT('adsTxt.records.manualEntry')}
              </Button>
            </div>
          ) : (
            <AdsTxtRecordFields
              value={value}
              demandPartners={demandPartners.data?.content ?? []}
              loadingDemandPartners={demandPartners.isLoading}
              pending={pending}
              lockIdentity={Boolean(record)}
              submitted={submitted}
              valid={valid}
              onChange={patch}
            />
          )}
        </div>
        <DialogFooter className="border-border border-t px-5 py-4">
          <Button type="button" variant="outline" disabled={pending} onClick={onClose}>
            {dashboardT('adsTxt.cancel')}
          </Button>
          <Button type="button" disabled={pending || !manual} onClick={() => void submit()}>
            {dashboardT('adsTxt.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function toPayload(record: AdsTxtRecord | null): AdsTxtRecordPayload {
  if (!record) return emptyValue;
  return {
    domain: record.domain,
    publisherAccountId: record.publisherAccountId,
    accountType: record.accountType,
    demandPartnerId: record.demandPartnerId ?? record.demandPartner?.id ?? '',
    certAuthority: record.certAuthority ?? null,
    priority: record.priority ?? null,
    comment: record.comment ?? null,
    note: record.note ?? null,
    web: record.web ?? false,
    ctv: record.ctv ?? false,
  };
}

function trimPayload(value: AdsTxtRecordPayload): AdsTxtRecordPayload {
  return {
    ...value,
    domain: value.domain.trim().toLowerCase(),
    publisherAccountId: value.publisherAccountId.trim(),
    demandPartnerId: value.demandPartnerId.trim(),
    certAuthority: value.certAuthority?.trim() || null,
    comment: value.comment?.trim() || null,
    note: value.note?.trim() || null,
  };
}
